import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { API_URL, formatCurrency, formatDate, getStatusColor, statusLabels } from "../../lib/utils";
import {
    FolderKanban,
    FileText,
    Receipt,
    TrendingUp,
    ArrowRight,
} from "lucide-react";

export const PortalDashboardPage = () => {
    const { user, token } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboard = async () => {
            try {
                const res = await fetch(`${API_URL}/portal/dashboard`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) setData(await res.json());
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboard();
    }, [token]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!data) return null;

    const { stats, recent_projects, recent_quotes, recent_invoices } = data;

    return (
        <div className="space-y-8 animate-fade-in" data-testid="portal-dashboard">
            {/* Welcome */}
            <div>
                <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                    Bienvenue, {user?.name || "Client"}
                </h1>
                <p className="text-muted-foreground mt-1">
                    Voici un aperçu de vos projets et documents
                </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <FolderKanban className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats.active_projects}</p>
                                <p className="text-xs text-muted-foreground">Projets actifs</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats.pending_quotes}</p>
                                <p className="text-xs text-muted-foreground">Devis en attente</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                <Receipt className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats.unpaid_invoices}</p>
                                <p className="text-xs text-muted-foreground">Factures à payer</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{formatCurrency(stats.total_paid)}</p>
                                <p className="text-xs text-muted-foreground">Total payé</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Recent Items */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Projects */}
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base font-heading">Projets récents</CardTitle>
                            <Button variant="ghost" size="sm" onClick={() => navigate("/portal/projects")}>
                                Voir tout <ArrowRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {recent_projects.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">Aucun projet</p>
                        ) : (
                            recent_projects.map((p) => (
                                <div
                                    key={p.project_id}
                                    className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                                    onClick={() => navigate(`/portal/projects/${p.project_id}`)}
                                    data-testid={`portal-project-${p.project_id}`}
                                >
                                    <div>
                                        <p className="text-sm font-medium">{p.name}</p>
                                        <p className="text-xs text-muted-foreground">{formatDate(p.created_at)}</p>
                                    </div>
                                    <Badge className={getStatusColor(p.status)}>{statusLabels[p.status] || p.status}</Badge>
                                </div>
                            ))
                        )}
                    </CardContent>
                </Card>

                {/* Recent Quotes */}
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base font-heading">Derniers devis</CardTitle>
                            <Button variant="ghost" size="sm" onClick={() => navigate("/portal/quotes")}>
                                Voir tout <ArrowRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {recent_quotes.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">Aucun devis</p>
                        ) : (
                            recent_quotes.map((q) => (
                                <div
                                    key={q.quote_id}
                                    className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                >
                                    <div>
                                        <p className="text-sm font-medium">{q.quote_number}</p>
                                        <p className="text-xs text-muted-foreground">{formatCurrency(q.total)}</p>
                                    </div>
                                    <Badge className={getStatusColor(q.status)}>{statusLabels[q.status] || q.status}</Badge>
                                </div>
                            ))
                        )}
                    </CardContent>
                </Card>

                {/* Recent Invoices */}
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base font-heading">Dernières factures</CardTitle>
                            <Button variant="ghost" size="sm" onClick={() => navigate("/portal/invoices")}>
                                Voir tout <ArrowRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {recent_invoices.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">Aucune facture</p>
                        ) : (
                            recent_invoices.map((inv) => (
                                <div
                                    key={inv.invoice_id}
                                    className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                                >
                                    <div>
                                        <p className="text-sm font-medium">{inv.invoice_number}</p>
                                        <p className="text-xs text-muted-foreground">{formatCurrency(inv.total)}</p>
                                    </div>
                                    <Badge className={getStatusColor(inv.status)}>{statusLabels[inv.status] || inv.status}</Badge>
                                </div>
                            ))
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
